import { Button } from "@/components/ui/button";
import { ErrorDisplay } from "@cohesive/ui-components";
import * as Sentry from "@sentry/react";
import { Component, ErrorInfo, ReactNode } from "react";
import { NavigateFunction, useNavigate } from "react-router-dom";

import { ROUTES } from "@/constants/routes";

interface Props {
  navigate: NavigateFunction;
  onError?: (error: Error) => void;
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

class ErrorBoundaryClass extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error(error, errorInfo);
    this.props.onError?.(error);
    Sentry.captureException(error, {
      extra: {
        errorInfo,
      },
    });
  }

  handleReset = () => {
    this.setState({ hasError: false, error: undefined });
    this.props.navigate(ROUTES.HOME);
  };

  render() {
    if (this.state.hasError) {
      return (
        <ErrorDisplay
          footer={<Button onClick={this.handleReset}>Go to Home</Button>}
        />
      );
    }

    return this.props.children;
  }
}

// Wrapper component to provide navigation
export function ErrorBoundary(props: Omit<Props, "navigate">) {
  const navigate = useNavigate();
  return <ErrorBoundaryClass navigate={navigate} {...props} />;
}
